import { useCallback, useEffect, useState } from "react";
import { notificationService } from "@/features/notifications/services/notification.service";
import type { NotificationRaw } from "@/features/notifications/modal/notification.types";
import { logger } from "@/lib";

const PAGE_SIZE = 100;

export const useAndyNotifications = (type: string) => {
  const [notifications, setNotifications] = useState<NotificationRaw[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const hasMore = notifications.length < total;

  const fetchAndyNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await notificationService.getAndyNotifications(1, PAGE_SIZE, type);
      setUnreadCount(response.data.unread_count ?? 0);
      if (response?.data?.items) {
        setNotifications(response.data.items);
        setTotal(response.data.total);
        setPage(1);
      } else {
        setNotifications([]);
        setTotal(0);
      }
    } catch (error) {
      logger.error("Andy notifications API error", error);
      setNotifications([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [type]);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllNotificationsRead(type);
      await fetchAndyNotifications();
    } catch (error) {
      logger.error("Mark all as read error", error);
    }
  }, [fetchAndyNotifications, type]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const response = await notificationService.getAndyNotifications(nextPage, PAGE_SIZE, type);
      if (response?.data?.items?.length) {
        setNotifications((prev) => [...prev, ...response.data.items]);
        setPage(nextPage);
      }
    } catch (error) {
      logger.error("Andy notifications load more error", error);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, page, type]);

  useEffect(() => {
    void fetchAndyNotifications();
  }, [fetchAndyNotifications]);

  return {
    notifications,
    loading,
    loadingMore,
    hasMore,
    total,
    unreadCount,
    fetchAndyNotifications,
    loadMore,
    markAllAsRead,
  };
};
